import type { SupabaseClient } from '@supabase/supabase-js'

// ============================================================
// Settings stored as JSONB on restaurants table
// ============================================================

export interface PartySizeDurationRule {
  min: number       // inclusive
  max: number       // inclusive
  minutes: number
}

export interface BookingSettings {
  confirm_mode: 'auto' | 'manual' | 'upto'
  upto_count: number
  reminders: boolean
  response_option: boolean
  required_fields: { name: boolean; phone: boolean; email: boolean }
  interval: string                 // minutes as string '5'..'300'
  min_advance: string              // minutes
  max_advance: string              // days
  show_end_time: boolean
  waiting_list: boolean
  security_code: boolean
  min_group: number
  max_group: number
  labels: Array<{ id: string; name: string; color: string }>
  time_slots: string[]
  custom_fields: string[]
  /** Per-party-size reservation duration. First matching rule wins. */
  duration_by_party_size: PartySizeDurationRule[]
  /** Default duration when no rule matches. */
  default_duration_minutes: number
  /**
   * When false, public widget rejects bookings for today's date in
   * Europe/Berlin time — the guest must pick tomorrow or later.
   * Owner-side dashboard bookings are unaffected. Default true so
   * existing restaurants don't break on rollout.
   */
  allow_same_day: boolean
  /**
   * Day 48 — Pacing cap: max guests that may newly arrive in a single
   * time slot, regardless of how many tables are free. Lets the owner
   * protect the kitchen/service from a rush where every table books the
   * same time. 0 = no cap (default, unchanged behaviour).
   */
  max_guests_per_slot: number
  /**
   * When true, a guest's self-service date/time/party-size change is NOT
   * applied instantly — it's queued as a pending request the owner approves
   * or rejects in the panel (the original booking holds until then). Default
   * false = instant change (unchanged behaviour). Notes always apply instantly.
   */
  guest_change_needs_approval: boolean
}

export const DEFAULT_BOOKING_SETTINGS: BookingSettings = {
  confirm_mode: 'auto',
  upto_count: 6,
  reminders: true,
  response_option: true,
  // Owner can opt out per restaurant later, but the safe default is to
  // require BOTH so reminder/cancellation mails actually have somewhere to go.
  required_fields: { name: true, phone: true, email: true },
  interval: '30',
  min_advance: '0',
  max_advance: '365',
  show_end_time: false,
  waiting_list: false,
  security_code: false,
  min_group: 1,
  max_group: 9,
  labels: [],
  time_slots: [],
  custom_fields: [],
  duration_by_party_size: [
    { min: 1, max: 3, minutes: 60 },
    { min: 4, max: 6, minutes: 90 },
    { min: 7, max: 99, minutes: 120 },
  ],
  default_duration_minutes: 90,
  allow_same_day: true,
  max_guests_per_slot: 0,
  guest_change_needs_approval: false,
}

/**
 * Pick the duration (in minutes) for a given party size.
 *
 * THE one place that resolves booking durations. Several API routes used to
 * roll their own lookup against a `max_party_size` field that never existed
 * in the stored rules ({min,max,minutes}) — the rules silently never matched
 * and everything fell back to the 90-minute default, so a 2-person booking
 * blocked/announced 1.5h although the owner had configured 60 minutes.
 * Tolerates the legacy field name anyway, in case old rows carry it.
 */
export function durationForPartySize(
  settings: Pick<BookingSettings, 'duration_by_party_size' | 'default_duration_minutes'>,
  guests: number,
): number {
  const rules = (settings.duration_by_party_size ?? []) as Array<
    Partial<PartySizeDurationRule> & { max_party_size?: number }
  >
  for (const r of rules) {
    const min = r.min ?? 1
    const max = r.max ?? r.max_party_size ?? 0
    if (typeof r.minutes === 'number' && guests >= min && guests <= max) return r.minutes
  }
  return settings.default_duration_minutes ?? 90
}

export interface IntegrationSettings {
  stripe_publishable: string
  paypal_client_id: string
  sepa_enabled: boolean
  gtm_enabled: boolean
  reserve_with_google: boolean
  obird_email: string
  obird_venue: string
  facebook_url: string
  instagram_url: string
  google_business_url: string
  webhooks: Array<{ id: string; url: string; events?: string[] }>
}

export const DEFAULT_INTEGRATION_SETTINGS: IntegrationSettings = {
  stripe_publishable: '',
  paypal_client_id: '',
  sepa_enabled: false,
  gtm_enabled: false,
  reserve_with_google: false,
  obird_email: '',
  obird_venue: '',
  facebook_url: '',
  instagram_url: '',
  google_business_url: '',
  webhooks: [],
}

export interface OrdersSettings {
  qr_code_enabled: boolean
  sound: '1' | '2' | '3' | '4'
  payment_methods: { cash: boolean; card: boolean; paypal: boolean; sepa: boolean }
  takeaway_slots: string[]
  delivery_slots: string[]
  takeaway_interval: '15' | '30' | '60'
  delivery_time_min: number
  min_order_amount: number
  delivery_fee: number
  qr_text: string
  qr_size: number
  /**
   * Optional intro animation (short logo video) for the guest QR menu.
   * Shown fullscreen ONCE per browser session on first open, always
   * skippable; empty string = feature off (default).
   */
  intro_video_url: string
  /**
   * QR menu layout. 'classic' = the original single-scroll list (default,
   * existing restaurants unchanged). 'cards' = photo category-card landing
   * (Menury-style) with contact icons, search and a DE/EN toggle; tapping
   * a card drops into the classic list.
   */
  menu_design: 'classic' | 'cards' | 'grouped'
  /** Contact links shown in the cards-design header (empty = icon hidden). */
  menu_links: { website: string; instagram: string }
  /**
   * Explicit cover image per menu category (category id → public URL) for
   * the cards design. Falls back to the first dish photo in the category.
   * Stored here (JSONB) so no schema change is needed on menu_categories.
   */
  category_images: Record<string, string>
  /**
   * Optional guest-facing description per menu category (category id →
   * text), shown under the category heading on the QR menu. JSONB —
   * no schema change on menu_categories needed.
   */
  category_descriptions: Record<string, string>
  /**
   * Show the "Speisekarte / name / address" text block over the QR-menu
   * hero. Restaurants whose hero image already carries their branding
   * (e.g. a logo banner) turn this off. Default true.
   */
  menu_show_header: boolean
  /**
   * 'grouped' design: top-level groups (Hauptkategorien) shown as the
   * landing cards; each regular menu category is assigned to one group and
   * appears as a sub-category inside it. JSONB — no schema change needed.
   */
  menu_groups: { id: string; name: string }[]
  /** category id → group id (categories without entry show standalone). */
  category_groups: Record<string, string>
  /** Explicit cover image per Hauptkategorie (group id → public URL);
   *  falls back to the first sub-category image. */
  group_images: Record<string, string>
  /**
   * Cards per row for the category/group card grids on tablet/desktop
   * (phones always show 2). 2, 3 or 4 — default 3 (previous behaviour).
   */
  menu_card_cols: number
  /**
   * Per-item option lines shown under the dish on the guest menu
   * (e.g. "Brötchen nach Wahl: Weizen / Dinkel", "+ Hafermilch 0,50 €").
   * item id → list of option strings.
   */
  item_options: Record<string, string[]>
}

export const DEFAULT_ORDERS_SETTINGS: OrdersSettings = {
  qr_code_enabled: false,
  sound: '1',
  payment_methods: { cash: true, card: false, paypal: false, sepa: false },
  takeaway_slots: [],
  delivery_slots: [],
  takeaway_interval: '30',
  delivery_time_min: 60,
  min_order_amount: 0,
  delivery_fee: 0,
  qr_text: 'Jetzt scannen und bestellen!',
  qr_size: 500,
  intro_video_url: '',
  menu_design: 'classic',
  menu_links: { website: '', instagram: '' },
  category_images: {},
  category_descriptions: {},
  menu_show_header: true,
  menu_groups: [],
  category_groups: {},
  group_images: {},
  menu_card_cols: 3,
  item_options: {},
}

// ============================================================
// Helpers
// ============================================================

export async function loadSettings<T>(
  supabase: SupabaseClient,
  slug: string,
  column: 'booking_settings' | 'integration_settings' | 'orders_settings',
  defaults: T,
): Promise<T> {
  const { data } = await supabase
    .from('restaurants')
    .select(column)
    .eq('slug', slug)
    .maybeSingle()
  if (!data) return defaults
  const raw = (data as Record<string, unknown>)[column]
  if (!raw || typeof raw !== 'object') return defaults
  return { ...defaults, ...(raw as object) } as T
}

export async function saveSettings<T>(
  supabase: SupabaseClient,
  slug: string,
  column: 'booking_settings' | 'integration_settings' | 'orders_settings',
  value: T,
): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from('restaurants')
    .update({ [column]: value })
    .eq('slug', slug)
  if (!error) return { error: null }
  // Friendlier messages for common Supabase failures
  const msg = error.message || 'Speichern fehlgeschlagen'
  if (msg.toLowerCase().includes(column)) {
    return { error: `Datenbankspalte "${column}" fehlt. Migration 20260508_settings_orders_customers.sql in Supabase ausführen.` }
  }
  if (error.code === '42501' || msg.toLowerCase().includes('row-level security')) {
    return { error: 'Berechtigungsfehler — RLS-Policy für restaurants UPDATE fehlt.' }
  }
  return { error: msg }
}
